import React from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import { STATUS_SUMMARY_INCIDENT_TYPES } from 'components/IAP/ICSConstants';

import SaveableField from '../../SaveableField';

type IncidentTypeCheckboxGroupProps = {
  statusSummaryId?: string;
  originalIncidentType?: string[];
  originalOtherIncidentType?: string;
  readOnly?: boolean;
};

const slugify = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const IncidentTypeCheckboxGroup: React.FC<IncidentTypeCheckboxGroupProps> = ({
  statusSummaryId,
  originalIncidentType,
  originalOtherIncidentType,
  readOnly = false,
}) => {
  const [incidentTypes, setIncidentTypes] = React.useState<string[]>(
    originalIncidentType || []
  );

  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const handleChange = (value: string[]) => {
    setIncidentTypes(value);
    upsertIncidentStatusSummary({ incidentType: value });
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <div
        role="group"
        aria-label="Type of Incident"
        className="tw:grid tw:gap-2 tw:sm:grid-cols-2 tw:lg:grid-cols-3"
      >
        {STATUS_SUMMARY_INCIDENT_TYPES.map((incidentType) => {
          const optionId = `ics209-incident-type-${slugify(incidentType.value)}`;
          return (
            <div
              key={incidentType.value}
              className="tw:flex tw:items-center tw:gap-2"
            >
              <Checkbox
                id={optionId}
                checked={incidentTypes.includes(incidentType.value)}
                disabled={readOnly}
                onCheckedChange={(checked) =>
                  handleChange(
                    checked
                      ? [...incidentTypes, incidentType.value]
                      : incidentTypes.filter((v) => v !== incidentType.value)
                  )
                }
              />
              <Label htmlFor={optionId} className="tw:font-normal">
                {incidentType.label}
              </Label>
            </div>
          );
        })}
      </div>

      {incidentTypes.includes('Other') && (
        <SaveableField
          id="otherIncidentType"
          label="Specify Other Incident Type"
          multiline
          maxLength={100}
          disabled={readOnly}
          value={originalOtherIncidentType}
          onSave={(value) =>
            upsertIncidentStatusSummary({ otherIncidentType: value })
          }
        />
      )}
    </div>
  );
};

export default IncidentTypeCheckboxGroup;
